import { forwardRef } from "react";
import {
  EditableDataGrid,
  type EditableDataGridHandle,
} from "@/components/editable-data-grid";
import { TemplateGridShell } from "@/components/submittal/template-grid-shell";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

interface ProcoreTemplateGridProps {
  headers: string[];
  rows: string[][];
  onSave?: (headers: string[], rows: string[][]) => Promise<void>;
  onCancel?: () => void;
  readOnly?: boolean;
  className?: string;
  selectable?: boolean;
  selectedRows?: Set<number>;
  onSelectionChange?: (rows: Set<number>) => void;
  cellRenderer?: (
    rowIndex: number,
    colIndex: number,
    value: string,
    onChange: (value: string) => void,
    rowValues: string[]
  ) => ReactNode | null;
  controls?: ReactNode;
  packageColumnIndex?: number;
  hideActionBar?: boolean;
  onHasChangesChange?: (hasChanges: boolean) => void;
  onIsSavingChange?: (isSaving: boolean) => void;
  showHeader?: boolean;
}

/**
 * Procore template wrapper component for the submittal data grid.
 * - Displays template name in header
 * - Provides sticky horizontal scroll bar through title area
 * - Applies Procore-specific styling
 * - Headers are not editable for Procore template
 */
export const ProcoreTemplateGrid = forwardRef<
  EditableDataGridHandle,
  ProcoreTemplateGridProps
>(function ProcoreTemplateGrid(
  {
    headers,
    rows,
    onSave,
    onCancel,
    readOnly = false,
    className,
    selectable = false,
    selectedRows,
    onSelectionChange,
    cellRenderer,
    controls,
    packageColumnIndex,
    hideActionBar,
    onHasChangesChange,
    onIsSavingChange,
    showHeader = true,
  },
  ref
) {
  return (
    <TemplateGridShell
      templateLabel="Procore Import Template"
      rowCount={rows.length}
      className={cn(className)}
      controls={controls}
      showHeader={showHeader}
    >
      <EditableDataGrid
        ref={ref}
        headers={headers}
        rows={rows}
        onSave={onSave}
        onCancel={onCancel}
        readOnly={readOnly}
        readOnlyHeaders={true}
        selectable={selectable}
        selectedRows={selectedRows}
        onSelectionChange={onSelectionChange}
        cellRenderer={cellRenderer}
        packageColumnIndex={packageColumnIndex}
        hideActionBar={hideActionBar}
        onHasChangesChange={onHasChangesChange}
        onIsSavingChange={onIsSavingChange}
      />
    </TemplateGridShell>
  );
});
